/**
 * Paper-doll equipment layout matching the OSRS classic silhouette:
 *
 *              [Head]
 *       [Cape] [Neck] [Ammo]
 *   [Weapon] [Body] [Shield]
 *              [Legs]
 *      [Gloves][Boots][Ring]
 *
 * Only `weapon` is functional today (backend has no other slots); the rest
 * render an empty silhouette placeholder inviting future gear.
 */

import { useMemo } from "react";
import { HardHat, Shield, Sword, Shirt, Hand, Footprints, Gem, Feather, X } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { ITEM_ICON } from "@/lib/gameData";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

// Custom simple SVG icons for slots Lucide doesn't cover well.
const CapeIcon = ({ size = 24, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M6 3l6 4 6-4v14l-6 4-6-4V3z" opacity="0.75" />
  </svg>
);
const LegsIcon = ({ size = 24, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M7 3h10v6l-1.5 12h-3l-.5-9h-1l-.5 9h-3L6 9V3z" opacity="0.75" />
  </svg>
);
const RingIcon = ({ size = 24, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="14" r="6" />
    <path d="M8.5 8L12 4l3.5 4" fill="currentColor" stroke="none" opacity="0.7" />
  </svg>
);

// Row/Column position for each slot in the 3-col × 5-row grid.
export const EQUIPMENT_LAYOUT = [
  { slot: "head",    label: "Head",       row: 1, col: 2, Icon: HardHat },
  { slot: "cape",    label: "Cape",       row: 2, col: 1, Icon: CapeIcon },
  { slot: "neck",    label: "Amulet",     row: 2, col: 2, Icon: Gem },
  { slot: "ammo",    label: "Ammo",       row: 2, col: 3, Icon: Feather },
  { slot: "weapon",  label: "Weapon",     row: 3, col: 1, Icon: Sword },
  { slot: "body",    label: "Body",       row: 3, col: 2, Icon: Shirt },
  { slot: "shield",  label: "Shield",     row: 3, col: 3, Icon: Shield },
  { slot: "legs",    label: "Legs",       row: 4, col: 2, Icon: LegsIcon },
  { slot: "gloves",  label: "Gloves",     row: 5, col: 1, Icon: Hand },
  { slot: "boots",   label: "Boots",      row: 5, col: 2, Icon: Footprints },
  { slot: "ring",    label: "Ring",       row: 5, col: 3, Icon: RingIcon },
];

function Slot({ def, itemId, itemDef, onUnequip, size = 56 }) {
  const Icon = def.Icon;
  const filled = !!itemId;
  return (
    <div
      className="relative group"
      style={{ gridRow: def.row, gridColumn: def.col }}
      data-testid={`equip-slot-${def.slot}`}
    >
      <div
        className="w-full h-full flex items-center justify-center rounded transition-colors"
        style={{
          height: size,
          background: filled
            ? "linear-gradient(180deg, #3a2f1e 0%, #2a2317 100%)"
            : "linear-gradient(180deg, #4a4030 0%, #3a3223 100%)",
          border: filled ? "1px solid rgb(180 140 60 / 0.6)" : "1px solid rgb(80 70 50 / 0.6)",
          boxShadow: "inset 0 0 0 1px rgba(30, 25, 15, 0.85), inset 0 0 12px rgba(0,0,0,0.35)",
        }}
        title={filled ? itemDef?.name : def.label}
      >
        {filled ? (
          <span className="text-3xl drop-shadow-[0_1px_0_rgba(0,0,0,0.9)]">
            {ITEM_ICON[itemId] || "◇"}
          </span>
        ) : (
          <Icon size={26} className="text-stone-800/90" strokeWidth={1.6} />
        )}
      </div>
      {filled && (
        <button
          onClick={onUnequip}
          data-testid={`unequip-${def.slot}`}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-stone-900 border border-stone-700 text-stone-400 hover:text-red-300 hover:border-red-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          title="Unequip"
        >
          <X size={11} strokeWidth={2.5} />
        </button>
      )}
      {/* Slot label under empty slots, subtle */}
      {!filled && (
        <span className="absolute -bottom-4 left-0 right-0 text-center text-[9px] uppercase tracking-widest text-stone-600 font-numeric hidden lg:block">
          {def.label}
        </span>
      )}
    </div>
  );
}

export function EquipmentDoll({ compact = false, showStats = true }) {
  const { snap, content, unequip } = useGame();
  const items = content?.items || {};
  const equipment = snap?.equipment || {};
  const size = compact ? 46 : 56;

  const stats = useMemo(() => {
    let atk = 0, str = 0, def = 0, spd = 0, count = 0;
    for (const [, id] of Object.entries(equipment)) {
      if (!id) continue;
      const it = items[id];
      if (!it) continue;
      count += 1;
      atk += it.atk || 0;
      str += it.str || 0;
      def += it.def || 0;
      spd += it.speed || 0;
    }
    return { atk, str, def, spd, count };
  }, [equipment, items]);

  async function doUnequip(slot) {
    try {
      await unequip(slot);
      toast.message("Unequipped.");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div
      className="rounded p-4 lg:p-5"
      style={{
        background: "linear-gradient(180deg, #3e3627 0%, #2a2418 60%, #1e1a12 100%)",
        boxShadow: "inset 0 0 0 1px rgba(60, 50, 28, 0.6), 0 4px 0 rgba(0,0,0,0.35)",
        border: "1px solid rgb(52 45 30)",
      }}
      data-testid="equipment-doll"
    >
      <div
        className="grid gap-3 mx-auto"
        style={{
          gridTemplateColumns: `repeat(3, ${size}px)`,
          gridTemplateRows: `repeat(5, ${size}px)`,
          width: "fit-content",
        }}
      >
        {EQUIPMENT_LAYOUT.map((def) => (
          <Slot
            key={def.slot}
            def={def}
            itemId={equipment[def.slot]}
            itemDef={items[equipment[def.slot]]}
            onUnequip={() => doUnequip(def.slot)}
            size={size}
          />
        ))}
      </div>

      {showStats && (
        <div className="mt-5 pt-3 border-t border-stone-800/60 flex flex-wrap justify-center gap-4 text-[11px] font-numeric text-stone-400">
          <span>Items · <span className="text-amber-300">{stats.count}</span></span>
          <span>+Atk <span className="text-amber-300">{stats.atk}</span></span>
          <span>+Str <span className="text-amber-300">{stats.str}</span></span>
          <span>+Def <span className="text-amber-300">{stats.def}</span></span>
          <span>Speed <span className="text-amber-300">{stats.spd || "—"}</span></span>
        </div>
      )}
    </div>
  );
}
